import {
  DEFAULT_FETCHING_NEWS_OPTIONS,
  fetchNewsArticle,
  fetchNewsArticles,
  useFetchNewsArticle,
  useFetchNewsArticles,
} from "@/apis/queryFunctions/news";
import { newsKeys } from "@/apis/queryKeys";
import AppBreadcrumbs from "@/common/components/Breadcrumbs";
import Container from "@/common/Layout/Container/Container";
import NewCard from "@/common/NewCard";
import sanitizeDOMData from "@/utils/sanitizeDOMData";
import slugify from "@/utils/slugifyString";
import {
  Affix,
  Box,
  Center,
  Grid,
  MediaQuery,
  Skeleton,
  Text,
  Title,
} from "@mantine/core";
import {
  useElementSize,
  useViewportSize,
  useWindowScroll,
} from "@mantine/hooks";
import { dehydrate, QueryClient } from "@tanstack/react-query";
import { NextSeo } from "next-seo";
import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";
import Toc from "./Toc";

export default function NewsDetail() {
  const [notFound, setNotFound] = React.useState(null);
  const {
    query: { slug },
    locale,
    asPath,
  } = useRouter();
  const breadcrumbs = [
    {
      title: locale === "vi" ? "Trang chủ" : "Home",
      href: "/",
    },
    {
      title: locale === "vi" ? "Tin tức" : "News",
      href: "/tin-tuc",
    },
  ];
  const { data: detail, isFetched } = useFetchNewsArticle(slug);
  const { data: news } = useFetchNewsArticles({
    limit: process.env.NEWS_LOADING_LIMIT,
    page: 1,
    condition: {
      ngon_ngu: locale,
      loai_tin_tuc: "Tin tức cà phê",
    },
  });
  // console.log("detail", detail);
  const refSideBar = React.useRef(null);
  const [rectSideBar, setRectSideBar] = React.useState(null);
  const { height, width } = useViewportSize();

  const { ref: rootRef, height: rootHeight } = useElementSize();
  const { ref: tocbotRef, height: tocbotHeight } = useElementSize();
  const [scroll] = useWindowScroll();

  const [runToc, setRunToc] = React.useState(false);
  React.useEffect(() => {
    if (!tocbotRef.current) return undefined;
    if (rootHeight > scroll.y + tocbotHeight) {
      tocbotRef.current.style.opacity = "1";
    } else {
      tocbotRef.current.style.opacity = "0";
    }
  }, [rootHeight, scroll, tocbotHeight]);
  React.useEffect(() => {
    if (refSideBar) {
      const rect = refSideBar?.current?.getBoundingClientRect();
      setRectSideBar(rect);
    }
  }, [height, width, refSideBar]);

  React.useEffect(() => {
    if (isFetched && !detail?.title) {
      setNotFound(
        <Center>
          <Title order={3}>
            {locale === "vi" ? "Tin tức không tồn tại" : "News does not exist"}
          </Title>
        </Center>
      );
    }
    if (isFetched && detail?.title) {
      const h1_elms = document?.querySelectorAll(".post h1");
      const h2_elms = document?.querySelectorAll(".post h2");
      const h3_elms = document?.querySelectorAll(".post h3");

      for (let i = 0; i < h1_elms.length; i++) {
        h1_elms[i].setAttribute("id", slugify(h1_elms[i].textContent));
      }
      for (let i = 0; i < h2_elms.length; i++) {
        h2_elms[i].setAttribute("id", slugify(h2_elms[i].textContent));
      }
      for (let i = 0; i < h3_elms.length; i++) {
        h3_elms[i].setAttribute("id", slugify(h3_elms[i].textContent));
      }
      setRunToc(true);
    }
  }, [isFetched, detail]);

  if (notFound) {
    return (
      <div>
        <Head>
          <title>
            {locale === "vi" ? "Tin tức không tồn tại" : "News does not exist"}
          </title>
        </Head>
        <Container>
          <Box py={20} style={{ minHeight: "100%" }}>
            {notFound}
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      <Head>
        <title>{detail?.title}</title>
      </Head>
      {detail && (
        <NextSeo
          title={detail?.meta_title}
          description={detail?.meta_description}
          openGraph={{
            title: detail?.meta_title || detail?.title,
            description: detail?.meta_description,
            type: "article",
            url: `${process.env.production_url}${asPath}`,
            article: {
              publishedTime: detail?.date_created,
              modifiedTime: detail?.date_updated,
              tags: detail?.meta_keywords?.split(" ") || [],
            },
            images: [
              {
                url: `${process.env.server_url}${detail?.picture}`,
                alt: detail?.title,
              },
            ],
          }}
        />
      )}
      <Container id="container">
        <Box py={20} style={{ minHeight: "100%" }}>
          <AppBreadcrumbs
            items={[...breadcrumbs, { title: detail?.title, href: "#" }]}
            isLoading={!detail}
          />
          <Grid>
            <Grid.Col xs={12} md={9}>
              <article>
                <Box mt={10} mb={10}>
                  <Skeleton visible={!detail}>
                    <Center>
                      <Title order={1} size="h5">
                        <Text>{detail?.title || "Bài viết không tồn tại"}</Text>
                      </Title>
                    </Center>
                  </Skeleton>
                </Box>

                <Box mb={10}>
                  <Skeleton visible={!detail}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Text size="sm" weight="500">
                        {detail?.tac_gia || locale === "vi"
                          ? "ZEMOR"
                          : "ZEMOR"}
                      </Text>
                      <Box mx={5}>|</Box>
                      <Text size="sm">
                        {new Date(detail?.date_updated).toLocaleDateString(
                          locale,
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          }
                        )}
                      </Text>
                    </Box>
                  </Skeleton>
                </Box>

                <div className="post webview">
                  {sanitizeDOMData(detail?.content, {})}
                  <h2>{locale === "vi" ? "Tin tức khác" : "Other news"}</h2>
                  <Grid>
                    {news && detail
                      ? news
                          .filter((item) => item._id !== detail?._id)
                          .slice(0, 3)
                          .map((item) => (
                            <Grid.Col key={item?._id} xs={12} sm={4} md={4}>
                              <NewCard item={item} />
                            </Grid.Col>
                          ))
                      : Array(3)
                          .fill(undefined)
                          .map((item, i) => (
                            <Grid.Col key={i} xs={12} sm={4} md={4}>
                              <NewCard item={item} isLoading key={i} />
                            </Grid.Col>
                          ))}
                  </Grid>
                </div>

                {!detail &&
                  Array(15)
                    .fill(undefined)
                    .map((item, i) => (
                      <div key={i}>
                        <Skeleton width="50%" height={14} my={5} />
                        <Skeleton width="70%" height={14} my={5} />
                        <Skeleton width="100%" height={14} my={5} />
                      </div>
                    ))}
              </article>
            </Grid.Col>
            <Grid.Col xs={12} md={3} ref={refSideBar}>
              {/* <Toc
                style={{ width: rectSideBar?.width }}
                refresh={Boolean(runToc && rectSideBar?.width)}
              /> */}
              <MediaQuery smallerThan="md" styles={{ display: "none" }}>
                <Affix
                  position={{
                    top: "140px",
                    left: `${rectSideBar?.left}px`,
                  }}
                  zIndex={10}
                >
                  <div ref={tocbotRef}>
                    <Toc
                      style={{
                        width: rectSideBar?.width,
                      }}
                      refresh={Boolean(runToc && rectSideBar?.width)}
                    />
                  </div>
                  {/* <div className="toc" style={{ width: rectSideBar?.width }} /> */}
                </Affix>
              </MediaQuery>
            </Grid.Col>
          </Grid>
          {/* {runToc && rectSideBar && <Toc />} */}
        </Box>
      </Container>
      <style jsx global>{`
        .sideBar {
          flex: 0 0 240px;
        }
        .sideBar-sticky {
          position: sticky;
          top: 2rem;
        }
      `}</style>
    </div>
  );
}

export async function getStaticPaths() {
  const newsArticles = await fetchNewsArticles({
    ...DEFAULT_FETCHING_NEWS_OPTIONS,
    limit: 100,
  });

  const paths = (newsArticles || []).map((newsArticle) => ({
    params: { slug: String(newsArticle.slug) },
  }));

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params = {} }) {
  const slug = params?.slug;
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(
    newsKeys.detail(slug),
    () => fetchNewsArticle(slug),
    {
      staleTime: 1000 * 60 * 60,
      enabled: !!slug,
    }
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
}
